import React from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';

/**
 * Select Component
 * 
 * A styled select dropdown with label, helper text, and error states.
 * Follows BankHub design system.
 * 
 * @example
 * ```tsx
 * <Select label="Category" placeholder="Choose category">
 *   <option value="food">Food</option>
 *   <option value="transport">Transport</option>
 * </Select>
 * ```
 */

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Label text */
  label?: string;
  /** Helper text displayed below select */
  helperText?: string;
  /** Error message (shows select as invalid) */
  error?: string;
  /** Placeholder option text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Make select full width */
  fullWidth?: boolean;
  /** Container className */
  containerClassName?: string;
  /** Options array (alternative to children) */
  options?: Array<{ value: string | number; label: string; disabled?: boolean }>;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      error,
      placeholder,
      size = 'md',
      fullWidth = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      required,
      options,
      children,
      ...props
    },
    ref
  ) => {
    // Generate ID if not provided
    const selectId = id || `select-${React.useId()}`;

    // Base select styles (using design tokens)
    const baseStyles = [
      'bg-ds-bg-surface border rounded-lg',
      'text-ds-text-primary',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:bg-ds-bg-muted disabled:cursor-not-allowed disabled:opacity-60',
      'appearance-none',
      'cursor-pointer',
    ].join(' ');

    // Error/normal state styles (using design tokens)
    const stateStyles = error
      ? [
          'border-ds-status-error-main',
          'focus:border-ds-status-error-main focus:ring-ds-status-error-main/20',
        ].join(' ')
      : [
          'border-ds-border-default',
          'focus:border-ds-primary-500 focus:ring-ds-primary-500/20',
        ].join(' ');

    // Size styles (adjusted for chevron icon) - MUCH MORE padding to prevent icon overlap
    const sizeStyles = {
      sm: 'pl-3 pr-12 py-1.5 text-sm',   // Increased to pr-12 (48px)
      md: 'pl-3 pr-14 py-2.5 text-base', // Increased to pr-14 (56px)
      lg: 'pl-4 pr-16 py-3 text-lg',     // Increased to pr-16 (64px)
    };

    // Width styles
    const widthStyles = fullWidth ? 'w-full' : '';

    // Container width
    const containerWidth = fullWidth ? 'w-full' : '';

    // Combine select styles
    const selectStyles = [
      baseStyles,
      stateStyles,
      sizeStyles[size],
      widthStyles,
      className,
    ].join(' ');

    // Chevron icon wrapper widths to reserve space on the right consistently
    const chevronWrapper = {
      sm: 'w-12',  // match pr-12
      md: 'w-14',  // match pr-14
      lg: 'w-16',  // match pr-16
    };

    return (
      <div className={`${containerWidth} ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-ds-text-primary mb-1.5"
          >
            {label}
            {required && <span className="text-ds-status-error-main ml-1">*</span>}
          </label>
        )}

        {/* Select Container */}
        <div className="relative">
          {/* Select */}
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            required={required}
            className={selectStyles}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error
                ? `${selectId}-error`
                : helperText
                ? `${selectId}-helper`
                : undefined
            }
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))
              : children}
          </select>

          {/* Chevron Icon or Error Icon - fixed-width right slot */}
          <div
            className={`absolute inset-y-0 right-0 ${chevronWrapper[size]} pointer-events-none z-10 flex items-center justify-center ${
              error ? 'text-ds-status-error-main' : 'text-ds-text-primary'
            }`}
          >
            {error ? (
              <AlertCircle size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} strokeWidth={2.5} />
            ) : (
              <ChevronDown size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} strokeWidth={2.5} />
            )}
          </div>
        </div>

        {/* Helper Text or Error Message */}
        {(helperText || error) && (
          <p
            id={error ? `${selectId}-error` : `${selectId}-helper`}
            className={`mt-1.5 text-sm ${
              error
                ? 'text-ds-status-error-dark'
                : 'text-ds-text-secondary'
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
